import { addToCart, getCartItems } from '../cart/_service'

/**
 * Лендинг подарочных сертификатов (российские города): выбор суммы
 * (фиксированные номиналы или своя сумма степпером ±1 000 ₽) и переход
 * в корзину, где сертификат донастраивается (дизайн, количество).
 */
export function initCertificatesLanding() {
  const root = document.querySelector<HTMLElement>(
    '[data-certificates-landing]'
  )
  if (!root) return

  const CUSTOM_STEP = 1000

  const customToggle = root.querySelector<HTMLElement>(
    '[data-custom-amount-toggle]'
  )
  const customValueEl = root.querySelector<HTMLElement>('[data-custom-value]')

  const customMax = Number(customToggle?.dataset.customMax) || Infinity
  let questPrices: number[] = []
  try {
    questPrices = JSON.parse(customToggle?.dataset.questPrices || '[]')
  } catch {
    questPrices = []
  }

  const state = { amount: null as number | null, customValue: 0 }

  const summary = root.querySelector<HTMLElement>('[data-amount-summary]')
  const summaryQuest = root.querySelector<HTMLElement>('[data-amount-quest]')
  const submitButton = root.querySelector<HTMLButtonElement>(
    '[data-landing-submit]'
  )

  const formatRub = (value: number) => `${value.toLocaleString('ru-RU')} ₽`

  const questWord = (count: number) =>
    count % 10 === 1 && count % 100 !== 11 ? 'квеста' : 'квестов'

  // Сколько квестов города можно оплатить этой суммой без доплат
  // (цена квеста ≤ номинала) — «купив сертификат на 10 000 ₽, выбирайте
  // любой из N квестов».
  const questLine = (amount: number) => {
    if (!questPrices.length) return ''
    const count = questPrices.filter((price) => price <= amount).length
    if (count === 0) return 'Любой квест дороже этой суммы — доплатите разницу'
    if (count === 1) return 'Этой суммы хватит на один квест без доплат'
    return `Этой суммы хватит на любой из ${count} ${questWord(count)} без доплат`
  }

  const syncSummary = () => {
    if (!summary || !submitButton) return

    if (state.amount) {
      summary.textContent = `Выбран электронный сертификат номиналом ${formatRub(state.amount)}`
      if (summaryQuest) {
        summaryQuest.textContent = questLine(state.amount)
        summaryQuest.hidden = !summaryQuest.textContent
      }
      submitButton.disabled = false
    } else {
      summary.textContent = 'Выберите номинал сертификата'
      if (summaryQuest) summaryQuest.hidden = true
      submitButton.disabled = true
    }
  }

  const syncCustom = () => {
    if (customValueEl) customValueEl.textContent = formatRub(state.customValue)
    if (customToggle?.classList.contains('is-active')) {
      state.amount =
        state.customValue > 0 && state.customValue <= customMax
          ? state.customValue
          : null
    }
    syncSummary()
  }

  const bumpCustom = (delta: number) => {
    if (!customToggle?.classList.contains('is-active')) {
      root
        .querySelectorAll('[data-amount].is-active')
        .forEach((el) => el.classList.remove('is-active'))
      customToggle?.classList.add('is-active')
    }
    state.customValue = Math.min(
      Math.max(state.customValue + delta, 0),
      customMax
    )
    syncCustom()
  }

  const selectFixedAmount = (card: HTMLElement) => {
    root
      .querySelectorAll('[data-amount].is-active')
      .forEach((el) => el.classList.remove('is-active'))
    customToggle?.classList.remove('is-active')
    card.classList.add('is-active')
    state.amount = Number(card.dataset.amount)
    syncSummary()
  }

  const openCustom = () => {
    root
      .querySelectorAll('[data-amount].is-active')
      .forEach((el) => el.classList.remove('is-active'))
    customToggle?.classList.add('is-active')
    syncCustom()
  }

  const submit = () => {
    if (!state.amount) return

    // В корзине может лежать белорусский сертификат (BYN): валюты в одном
    // заказе не смешиваются, рублёвый сертификат туда не добавляем.
    if (getCartItems().some((item) => item.currency === 'BYN')) {
      if (summaryQuest) {
        summaryQuest.textContent =
          'В корзине сертификат в белорусских рублях — сначала оформите его отдельным заказом'
        summaryQuest.hidden = false
      }
      return
    }

    addToCart({
      id: Date.now(),
      title: 'Электронный сертификат',
      type: 'digital',
      quantity: 1,
      price: state.amount,
      parameters: {},
      currency: 'RUB',
      image: ''
    })

    window.location.assign('/cart')
  }

  // Плавный, но быстрый скролл к якорным блокам страницы (hero-кнопки).
  const smoothScrollTo = (element: HTMLElement) => {
    const startY = window.scrollY
    const targetY = startY + element.getBoundingClientRect().top
    const duration = 450
    const startedAt = performance.now()

    const tick = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      window.scrollTo(0, startY + (targetY - startY) * eased)
      if (progress < 1) requestAnimationFrame(tick)
    }

    requestAnimationFrame(tick)
  }

  // Номинал по умолчанию помечен сервером (is-active) — подхватываем его в состояние.
  const preselected = root.querySelector<HTMLElement>('[data-amount].is-active')
  if (preselected) {
    state.amount = Number(preselected.dataset.amount)
    syncSummary()
  }

  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement

    const anchor = target.closest<HTMLAnchorElement>('a[href^="#"]')
    if (anchor) {
      const hash = anchor.getAttribute('href')
      const element = hash && document.querySelector<HTMLElement>(hash)
      if (element) {
        event.preventDefault()
        smoothScrollTo(element)
      }
      return
    }

    if (target.closest('[data-custom-minus]')) {
      bumpCustom(-CUSTOM_STEP)
      return
    }

    if (target.closest('[data-custom-plus]')) {
      bumpCustom(CUSTOM_STEP)
      return
    }

    const amountCard = target.closest<HTMLElement>('[data-amount]')
    if (amountCard) {
      selectFixedAmount(amountCard)
      return
    }

    if (target.closest('[data-custom-amount-toggle]')) {
      openCustom()
      return
    }

    if (target.closest('[data-landing-submit]')) {
      submit()
    }
  })
}
